'use client';
import React, { useState, useEffect } from 'react';

const slides = [
{
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_17a046a01-1772542022487.png",
  alt: 'Marrakech medina with traditional Moroccan architecture and vibrant souks at golden hour',
  location: 'Marrakech',
  tagline: 'The Red City'
},
{
  image: "https://images.unsplash.com/photo-1636459932653-74f87c406e57",
  alt: 'Sahara Desert golden sand dunes at sunset with camel caravan silhouettes in Morocco',
  location: 'Sahara Desert',
  tagline: 'The Golden Dunes'
},
{
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1299740e0-1784231440278.png",
  alt: 'Chefchaouen blue painted streets and buildings in the Rif Mountains of Morocco',
  location: 'Chefchaouen',
  tagline: 'The Blue Pearl'
},
{
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1d05318a2-1773214096424.png',
  alt: 'Fes ancient medina with historic Jewish quarter and traditional Moroccan architecture',
  location: 'Fes',
  tagline: 'The Spiritual Capital'
}];


const stats = [
{ value: '15+', label: 'Years Experience' },
{ value: '500+', label: 'Happy Clients' },
{ value: '20+', label: 'Destinations' },
{ value: '5★', label: 'Rated Service' }];


export default function Hero() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
        setIsTransitioning(false);
      }, 300);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const handleNavClick = (href: string) => {
    const el = document.querySelector(href);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <section id="home" className="relative h-screen min-h-[700px] flex items-center justify-center overflow-hidden">
      {/* Background Slides */}
      {slides.map((slide, index) =>
      <div
        key={index}
        className={`absolute inset-0 transition-opacity duration-1500 ${
        index === currentSlide ? 'opacity-100' : 'opacity-0'}`
        }
        style={{ transitionDuration: '1500ms' }}>
        
          <img
          src={slide.image}
          alt={slide.alt}
          className="w-full h-full object-cover"
          loading={index === 0 ? 'eager' : 'lazy'} />
        
        </div>
      )}

      {/* Multi-layer Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-navy-dark/65 via-navy-dark/35 to-navy-dark/80" />
      <div className="absolute inset-0 bg-gradient-to-r from-navy-dark/30 via-transparent to-navy-dark/20" />

      {/* Gold Top Line */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gold-gradient z-20" />

      {/* Current Location Badge */}
      <div className="absolute top-24 right-8 z-20 hidden md:block">
        <div className="bg-gold/10 backdrop-blur-sm border border-gold/30 px-4 py-2 text-right">
          <div className="font-sans text-xs text-gold uppercase tracking-widest">
            {slides[currentSlide]?.tagline}
          </div>
          <div className="font-serif text-sm font-bold text-white">
            {slides[currentSlide]?.location}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 container-luxury text-center px-4 pt-16">
        {/* Pre-headline Tag */}
        <div className="inline-flex items-center gap-3 mb-6 animate-fade-in">
          <div className="w-10 h-px bg-gold" />
          <span className="font-sans text-xs font-bold tracking-widest uppercase text-gold">
            Licensed Professional Guide · Morocco
          </span>
          <div className="w-10 h-px bg-gold" />
        </div>

        {/* Main Headline */}
        <h1
          className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6 text-shadow-luxury animate-fade-up"
          style={{ animationDelay: '200ms' }}>
          
          Private Luxury Tours &
          <br />
          <span className="text-gold italic">Jewish Heritage</span>
          <br />
          Experiences in Morocco
        </h1>

        {/* Ornamental Divider */}
        <div className="flex items-center justify-center gap-3 mb-6 animate-fade-up" style={{ animationDelay: '400ms' }}>
          <div className="w-16 h-px bg-gold/60" />
          <div className="w-2 h-2 bg-gold rotate-45" />
          <div className="w-2 h-2 border border-gold rotate-45" />
          <div className="w-2 h-2 bg-gold rotate-45" />
          <div className="w-16 h-px bg-gold/60" />
        </div>

        {/* Subheadline */}
        <p
          className="font-sans text-base sm:text-lg md:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed mb-10 text-shadow-sm animate-fade-up"
          style={{ animationDelay: '500ms' }}>
          
          Discover Morocco with David — a licensed professional guide offering private luxury tours,
          Jewish heritage journeys, chauffeur services, airport transfers, and tailor-made travel
          experiences across the Kingdom of Morocco.
        </p>

        {/* CTA Buttons */}
        <div
          className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-up"
          style={{ animationDelay: '700ms' }}>
          
          <button
            onClick={() => handleNavClick('#contact')}
            className="btn-gold animate-pulse-gold">
            
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Book Your Tour
          </button>

          <a
            href="https://wa.me/212668702424?text=Hello%20David%2C%20I%20am%20interested%20in%20a%20private%20tour%20in%20Morocco."
            target="_blank"
            rel="noopener noreferrer"
            className="btn-whatsapp">
            
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            Contact on WhatsApp
          </a>

          <button
            onClick={() => handleNavClick('#services')}
            className="btn-outline-gold">
            
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            Explore Services
          </button>
        </div>

        {/* Stats Bar */}
        <div
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto animate-fade-up"
          style={{ animationDelay: '900ms' }}>
          
          {stats.map((stat) =>
          <div key={stat.label} className="text-center">
              <div className="font-serif text-3xl font-bold text-gold">{stat.value}</div>
              <div className="font-sans text-xs text-white/80 uppercase tracking-wider mt-1">{stat.label}</div>
            </div>
          )}
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {slides.map((_, index) =>
        <button
          key={index}
          onClick={() => goToSlide(index)}
          className={`transition-all duration-300 ${
          index === currentSlide ?
          'w-8 h-1.5 bg-gold' : 'w-3 h-1.5 bg-white/50 hover:bg-white/80'}`
          }
          aria-label={`Go to slide ${index + 1}`} />

        )}
      </div>

      {/* Scroll Down Indicator */}
      <div className="absolute bottom-8 right-8 flex flex-col items-center gap-2 z-10">
        <span className="font-sans text-xs text-white/60 uppercase tracking-widest" style={{ writingMode: 'vertical-rl' }}>Scroll</span>
        <div className="w-px h-12 bg-gradient-to-b from-gold to-transparent animate-scroll-down" />
      </div>
    </section>);

}